import { Link } from 'react-router-dom'
import { SensitiveOverlay } from '@/components/SensitiveImage'
import { sensitiveImageClass, useSensitiveMask } from '@/hooks'
import type { CategoryTileModel } from '@/types/ui'
import { cn } from '@/utils/cn'

/**
 * 分类磁贴（设计稿 D1 中间那一排深色卡片）。
 *
 * 视觉特征值得说明：
 *   - 底色是 Deep Navy，**深浅两套主题下都是深色** —— 它靠色相与页面区分，
 *     不靠明暗，所以用 `navy` 这个原色 token 而不是 `surface`；
 *   - 右上角有一块主色的斜切角。用 `clip-path` 的三角形而不是旋转的方块，
 *     因为旋转会溢出圆角边界，需要额外的 overflow 处理；
 *   - 标题压在左下角。
 *
 * ## 有封面时的两处处理
 *
 * 封面是运营在展示位上配的（`GET /featured-tags` 的 `cover_public_key`，
 * 2026-08-11 起可用），没配到的格子仍然是纯色方块 —— 那是**常态外观**，
 * 不是加载失败。
 *
 * 1. **标题下面永远压一层深色渐变**，而不是「亮图才加遮罩」。图是运营任意上传的，
 *    前端无从得知它左下角亮不亮；只在某些图上加遮罩，等于让文字的可读性取决于
 *    这张图，而不取决于代码。
 * 2. 图片走 `object-cover` + `aspect` 由外层锁定，因此任何比例的原图都不会把
 *    这一排方块撑歪。
 */
export function CategoryTile({ tile, className }: { tile: CategoryTileModel; className?: string }) {
  const masked = useSensitiveMask(tile.coverUrl, tile.coverSensitive)

  return (
    <Link
      to={tile.to}
      className={cn(
        'group relative flex aspect-[4/3] items-end overflow-hidden rounded-card bg-navy p-3 transition-transform duration-200 ease-out-soft hover:-translate-y-0.5 sm:aspect-[5/4]',
        className,
      )}
    >
      {tile.coverUrl && (
        <>
          <img
            src={tile.coverUrl}
            alt=""
            loading="lazy"
            decoding="async"
            className={cn(
              'absolute inset-0 size-full object-cover transition-transform duration-300 ease-out-soft',
              masked ? sensitiveImageClass : 'group-hover:scale-[1.03]',
            )}
          />
          {/* 见文件头：与图片内容无关地固定压一层，保证左下角的标题始终可读。 */}
          <span
            aria-hidden="true"
            className="absolute inset-0 bg-gradient-to-t from-navy/85 via-navy/35 to-transparent"
          />
          {masked && <SensitiveOverlay url={tile.coverUrl} compact />}
        </>
      )}

      {/* 右上角斜切：装饰性，hover 时放大一点作为反馈。 */}
      <span
        aria-hidden="true"
        className="absolute -right-px -top-px size-14 bg-primary transition-transform duration-200 ease-out-soft group-hover:scale-110"
        style={{ clipPath: 'polygon(100% 0, 100% 100%, 0 0)' }}
      />
      <span className="relative text-sm font-semibold text-navy-foreground">{tile.label}</span>
    </Link>
  )
}
